import { format, differenceInMinutes } from 'date-fns';
import { toDate } from '../../../utils/timeUtils';
import { cn } from '../../../utils/cn';

/**
 * ProductionRunCard Component
 *
 * Displays a single production run card on the timeline.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.productionRun - The production run object
 * @param {Date} props.minDate - The minimum date of the timeline
 * @param {Date} props.maxDate - The maximum date of the timeline
 * @param {number} props.totalDurationMinutes - Total duration of the timeline in minutes
 * @param {Function} props.onProductionRunClick - Handler when the card is clicked
 */
export default function ProductionRunCard({
  productionRun,
  minDate,
  maxDate,
  totalDurationMinutes,
  onProductionRunClick,
}) {
  if (!productionRun.start_date || !productionRun.end_date) {
    return null;
  }

  const productionRunStartDate = toDate(productionRun.start_date);
  const productionRunEndDate = toDate(productionRun.end_date);

  if (!productionRunStartDate || !productionRunEndDate) {
    return null;
  }

  const visibleStartDate = productionRunStartDate < minDate ? minDate : productionRunStartDate;
  const visibleEndDate = productionRunEndDate > maxDate ? maxDate : productionRunEndDate;

  if (visibleStartDate >= visibleEndDate || productionRunEndDate < minDate || productionRunStartDate > maxDate) {
    return null;
  }

  // Calculate position and width relative to minDate (same as hour markers)
  const startOffset = differenceInMinutes(visibleStartDate, minDate);
  const duration = differenceInMinutes(visibleEndDate, visibleStartDate);

  const leftPercent = (startOffset / totalDurationMinutes) * 100;
  const widthPercent = (duration / totalDurationMinutes) * 100;

  const now = new Date();
  const isOngoing = now >= productionRunStartDate && now <= productionRunEndDate;
  const isPending = now < productionRunStartDate;

  return (
    <div
      className={cn(
        'absolute h-full rounded-lg transition-all duration-200 overflow-hidden box-border border',
        isOngoing ? 'cursor-pointer' : 'cursor-default',
        isOngoing
          ? 'border-[#51BE9A] dark:border-[#10B9814D] bg-[linear-gradient(90deg,_rgba(0,166,111,0.40)_0%,_rgba(119,237,194,0.40)_100%)] dark:bg-[linear-gradient(90deg,_rgba(16,185,129,0.40)_0%,_rgba(52,211,153,0.40)_100%)]'
          : isPending
            ? 'border-[#CAB72E] dark:border-[#F59E0B4D] bg-[linear-gradient(90deg,_rgba(194,175,0,0.40)_0%,_rgba(255,240,102,0.40)_133.9%)] dark:bg-[linear-gradient(90deg,_rgba(245,158,11,0.40)_0%,_rgba(250,204,21,0.40)_100%)]'
            : 'border-[#B9BAC0] dark:border-[rgba(185,186,192,0.20)] bg-[linear-gradient(90deg,_#D4D4D4_0%,_#F4F4F4_100%)] dark:bg-[linear-gradient(90deg,_rgba(212,212,212,0.30)_0%,_rgba(244,244,244,0.30)_100%)] shadow-[0_0_20px_0_rgba(245,158,11,0.10)]',
      )}
      style={{
        left: `${leftPercent}%`,
        width: `${widthPercent}%`,
        minWidth: '4px',
      }}
      onClick={() => isOngoing && onProductionRunClick && onProductionRunClick(productionRun)}
      title={`${productionRun.product_name || 'Production Run'} ${productionRun.note ? `\n${productionRun.note}` : ''}`}
    >
      <div className='relative rounded-lg pl-4 pr-1 py-6 box-border h-full w-full'>
        <div className='flex flex-col gap-1.5'>
          <span
            className={cn(
              'text-xs font-medium truncate',
              isOngoing
                ? 'text-[#007750] dark:text-[#A7F3D0CC]'
                : isPending
                  ? 'text-[#918400] dark:text-[#FDE68ACC]'
                  : 'text-[#737580] dark:text-[#E2E8F0CC]',
            )}
          >
            {format(productionRunStartDate, 'HH:mm')} - {format(productionRunEndDate, 'HH:mm')}
          </span>
          {productionRun.product_name && (
            <span
              className={cn(
                'text-base font-bold leading-tight truncate',
                isOngoing
                  ? 'text-[#005D3F] dark:text-[#34D399]'
                  : isPending
                    ? 'text-[#706601] dark:text-[#FBBF24]'
                    : 'text-[#737580] dark:text-[#E2E8F0]',
              )}
            >
              {productionRun.product_name}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
